/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useEffect, useRef, useState} from 'react';
import ThreadMonitor from '../../../models/threadMonitorModel';
import {AwesomeIcon, copyClipboard, ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {ThreadStatus} from '../../../util/interfaces';
import {changeSearchParam} from '../../../util/utils';
import Threads from './threads';
import {onSetCrumbs} from '../../../util/events';
import {ADMIN_ROUTE, ADMIN_THREADS_ROUTE} from '../../../util/constants';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

const THREADS_LIMIT = 20,
    LOAD_TIMEOUT = 5000;

export default function ThreadsController() {
    const {t} = useTranslation();
    const loadTimeout = useRef<number | null>(null);
    const params = new URLSearchParams(location.search);
    let _page: number = 0;
    if (params.has('page')) _page = parseInt(params.get('page') || '1') - 1;

    const [threads, setThreads] = useState<ThreadMonitor[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(_page);
    const [lastUpdated, setLastUpdated] = useState('');
    const [activeThreads, setActiveThreads] = useState(0);
    const [total, setTotal] = useState(0);

    function onReplaceThread(thread: ThreadMonitor) {
        const _threads = threads.slice();
        for (let i = 0; i < _threads.length; i++) {
            if (_threads[i].id === thread.id) {
                _threads.splice(i, 1, thread);
                return setThreads(_threads);
            }
        }
    }

    function onStopThread(thread: ThreadMonitor) {
        return function () {
            showDialog(
                t('admin.threads.stopConfirm', 'Weet je zeker dat je deze thread wilt stoppen?'),
                t('admin.threads.stopTitle', 'Thread stoppen'),
                [t('admin.threads.no', 'Nee'), {label: t('admin.threads.yes', 'Ja'), onClick: onConfirmStop(thread)}]
            );
        };
    }

    function onConfirmStop(thread: ThreadMonitor) {
        return function () {
            if (thread.status === ThreadStatus.RUNNING) {
                setLoading(true);
                thread.stop((thread: ThreadMonitor, success: boolean, data: ResponseData) => {
                    setLoading(false);
                    if (success) {
                        showSnackbar(t('admin.threads.stoppingSnack', 'De thread wordt gestopt'));
                        onReplaceThread(thread);
                    } else
                        showDialog(
                            data.message ||
                                t('admin.threads.stopFailedMsg', 'Het stoppen van de thread is niet gelukt.'),
                            t('admin.threads.stopFailedTitle', 'Stoppen mislukt')
                        );
                });
            }
        };
    }

    function onFinishThread(thread: ThreadMonitor) {
        return function () {
            if (thread.status !== ThreadStatus.FINISHED) {
                setLoading(true);
                thread.finish((thread: ThreadMonitor, success: boolean, data: ResponseData) => {
                    setLoading(false);
                    if (success) {
                        showSnackbar(t('admin.threads.finishedSnack', 'De thread is beëindigd'));
                        onReplaceThread(thread);
                    } else
                        showDialog(
                            data.message ??
                                t('admin.threads.finishFailedMsg', 'Het beëindigen van de thread is niet gelukt.'),
                            t('admin.threads.finishFailedTitle', 'Beëindigen mislukt')
                        );
                });
            }
        };
    }

    function onCopyId(id: string) {
        return function () {
            copyClipboard(id).then(() => {
                showSnackbar(
                    <label>
                        <AwesomeIcon name='check' style={{fontSize: '.9rem'}} />{' '}
                        {t('admin.threads.idCopied', 'ID gekopieerd naar klembord')}
                    </label>,
                    {backgroundColor: 'color-green-whatsapp'}
                );
            });
        };
    }

    function onChangePage(_page: number) {
        return function () {
            if (_page === page) return;
            changeSearchParam('page', (_page + 1).toString());
            setPage(_page);
        };
    }

    function onClose() {
        history.back();
    }

    function clearTimeout() {
        if (loadTimeout.current !== null) {
            window.clearTimeout(loadTimeout.current);
            loadTimeout.current = null;
        }
    }

    function loadThreads(showLoad: boolean) {
        if (!loading && showLoad) setLoading(true);
        clearTimeout();
        ThreadMonitor.getAll(THREADS_LIMIT, page, (threads: ThreadMonitor[], success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                const {total, active_threads} = data;
                setTotal(total);
                setActiveThreads(active_threads);
                setLastUpdated(IDate.now().format('HH:mm:ss'));
                setThreads(threads);
                loadTimeout.current = window.setTimeout(() => {
                    loadThreads(false);
                }, LOAD_TIMEOUT);
            }
        });
    }

    useEffect(() => {
        onSetCrumbs([
            {label: t('admin.threads.crumb.admin', 'Admin'), route: ADMIN_ROUTE},
            {label: t('admin.threads.crumb.threads', 'Threads'), route: ADMIN_THREADS_ROUTE}
        ]);
        return () => {
            clearTimeout();
        };
    }, []);

    useEffect(() => {
        loadThreads(true);
        return () => {
            clearTimeout();
        };
    }, [page]);

    return (
        <Threads
            onStopThread={onStopThread}
            onFinishThread={onFinishThread}
            onCopyId={onCopyId}
            onChangePage={onChangePage}
            onClose={onClose}
            threads={threads}
            page={page}
            loading={loading}
            lastUpdated={lastUpdated}
            activeThreads={activeThreads}
            total={total}
            limit={THREADS_LIMIT}
        />
    );
}
